import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";


import appCss from "../styles.css?url";
import i18n, { syncLanguageWithClientPreference } from "@/lib/i18n";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

// On the server (e.g. Cloudflare Worker), the i18n singleton is shared across
// requests. Force English at the start of every render so SSR output is
// deterministic and matches the initial client hydration pass.
if (typeof window === "undefined" && i18n.language !== "en") {
  i18n.changeLanguage("en");
}

function NotFoundComponent() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">{t("notFound.title")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t("notFound.desc")}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("notFound.home")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">{t("error.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("error.desc")}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            {t("error.retry")}
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {t("error.home")}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Comm42 SA" },
      { name: "description", content: "Turning strategy into measurable performance — worldwide." },
      { name: "author", content: "Comm42 SA" },
      { property: "og:title", content: "Comm42 SA" },
      { property: "og:description", content: "Turning strategy into measurable performance — worldwide." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Comm42" },
      { name: "twitter:title", content: "Comm42 SA" },
      { name: "twitter:description", content: "Turning strategy into measurable performance — worldwide." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/5d452620-cc5c-47e8-8856-77ce605f06cf/id-preview-7d187b30--cde75ee8-1a05-460f-8246-97d06c0bedc3.lovable.app-1778572574478.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/5d452620-cc5c-47e8-8856-77ce605f06cf/id-preview-7d187b30--cde75ee8-1a05-460f-8246-97d06c0bedc3.lovable.app-1778572574478.png" },
      { name: "google-site-verification", content: "6tBD0AH9K164LxvOYLJVHIiDotZn99iOuKqetS1I10o" },
      {
        httpEquiv: "Content-Security-Policy",
        content:
          "default-src 'self' https: data: blob:; " +
          "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://www.googletagmanager.com https://www.google-analytics.com https://www.google.com https://www.gstatic.com https://googleads.g.doubleclick.net https://cdn.gpteng.co https://cs.iubenda.com https://cdn.iubenda.com; " +
          "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; " +
          "font-src 'self' data: https://fonts.gstatic.com; " +
          "img-src 'self' data: blob: https:; " +
          "connect-src 'self' https: wss:; " +
          "frame-src 'self' https://www.google.com https://td.doubleclick.net https://googleads.g.doubleclick.net https://cdn.iubenda.com; " +
          "object-src 'none'; " +
          "base-uri 'self'; " +
          "form-action 'self';",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Inter:wght@300;400;500;600&display=swap",
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
    scripts: [
      {
        type: "text/javascript",
        children:
          "var _iub = _iub || []; _iub.csConfiguration = {\"siteId\":4585277,\"cookiePolicyId\":73384691,\"lang\":\"en\",\"storage\":{\"useSiteId\":true}};",
      },
      {
        type: "text/javascript",
        src: "https://cs.iubenda.com/autoblocking/4585277.js",
      },
      {
        type: "text/javascript",
        src: "https://cdn.iubenda.com/cs/gpp/stub.js",
      },
      {
        type: "text/javascript",
        src: "https://cdn.iubenda.com/cs/iubenda_cs.js",
        charset: "UTF-8",
        async: true,
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=AW-17485271426",
        async: true,
      },
      {
        children:
          "window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'AW-17485271426'); gtag('config', 'G-BE6WPEMFVR');",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Organization", "LocalBusiness", "ProfessionalService"],
          "@id": "https://comm42.ch/#organization",
          name: "Comm42 SA",
          legalName: "Comm42 SA",
          url: "https://comm42.ch",
          logo: "https://comm42.ch/favicon.ico",
          image: "https://comm42.ch/favicon.ico",
          description: "Strategic consulting, legal determination, investigation and international logistics.",
          slogan: "Investigate · Protect · Support",
          priceRange: "$$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "a la brona de Darba 7",
            postalCode: "6563",
            addressLocality: "Mesocco",
            addressRegion: "Canton Grigioni",
            addressCountry: "CH",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 46.4001,
            longitude: 9.2453,
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
          areaServed: [
            { "@type": "Country", name: "Switzerland" },
            { "@type": "Country", name: "Italy" },
            { "@type": "Place", name: "Worldwide" },
          ],
          contactPoint: [
            {
              "@type": "ContactPoint",
              telephone: "+41918311215",
              email: "sales@comm42.eu",
              contactType: "sales",
              areaServed: "Worldwide",
              availableLanguage: ["en", "it"],
            },
          ],
          sameAs: ["https://www.comm42.eu"],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  if (typeof window === "undefined" && i18n.language !== "en") {
    i18n.changeLanguage("en");
  }
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  useEffect(() => {
    syncLanguageWithClientPreference();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}
